/**
 * i18n configuration.
 *
 * - EN + HE from day one; HE triggers RTL on <html>.
 * - One namespace per feature so translation files stay short.
 * - Missing keys emit a dev-console warning (never show the raw key
 *   to users — fall back to EN).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import enVault from './locales/en/vault.json';
import enSpark from './locales/en/spark.json';
import enVerify from './locales/en/verify.json';
import enMarketplace from './locales/en/marketplace.json';
import enBlueprint from './locales/en/blueprint.json';
import enAttestations from './locales/en/attestations.json';
import enCommand from './locales/en/command.json';
import enCoach from './locales/en/coach.json';
import enConnectors from './locales/en/connectors.json';
import enOmega from './locales/en/omega.json';
import enErrors from './locales/en/errors.json';
import enSegments from './locales/en/segments.json';
import enDashboard from './locales/en/dashboard.json';
import enAuth from './locales/en/auth.json';
import enWizards from './locales/en/wizards.json';

import heCommon from './locales/he/common.json';
import heVault from './locales/he/vault.json';
import heSpark from './locales/he/spark.json';
import heVerify from './locales/he/verify.json';
import heMarketplace from './locales/he/marketplace.json';
import heBlueprint from './locales/he/blueprint.json';
import heAttestations from './locales/he/attestations.json';
import heCommand from './locales/he/command.json';
import heCoach from './locales/he/coach.json';
import heConnectors from './locales/he/connectors.json';
import heOmega from './locales/he/omega.json';
import heErrors from './locales/he/errors.json';
import heSegments from './locales/he/segments.json';
import heDashboard from './locales/he/dashboard.json';
import heAuth from './locales/he/auth.json';
import heWizards from './locales/he/wizards.json';

export const LOCALES = ['en', 'he'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  he: 'עברית',
};

export const RTL_LOCALES: ReadonlySet<Locale> = new Set(['he']);

export function isRtl(locale: Locale): boolean {
  return RTL_LOCALES.has(locale);
}

const resources = {
  en: {
    common: enCommon,
    vault: enVault,
    spark: enSpark,
    verify: enVerify,
    marketplace: enMarketplace,
    blueprint: enBlueprint,
    attestations: enAttestations,
    command: enCommand,
    coach: enCoach,
    connectors: enConnectors,
    omega: enOmega,
    errors: enErrors,
    segments: enSegments,
    dashboard: enDashboard,
    auth: enAuth,
    wizards: enWizards,
  },
  he: {
    common: heCommon,
    vault: heVault,
    spark: heSpark,
    verify: heVerify,
    marketplace: heMarketplace,
    blueprint: heBlueprint,
    attestations: heAttestations,
    command: heCommand,
    coach: heCoach,
    connectors: heConnectors,
    omega: heOmega,
    errors: heErrors,
    segments: heSegments,
    dashboard: heDashboard,
    auth: heAuth,
    wizards: heWizards,
  },
};

await i18n
  .use(initReactI18next)
  .init({
    resources,
    // English-only product: pin the active + supported language to English so no
    // screen ever renders the Hebrew bundle (the `he` resources stay on disk as
    // dormant infrastructure but are never activated).
    lng: 'en',
    fallbackLng: 'en',
    supportedLngs: ['en'],
    defaultNS: 'common',
    ns: ['common', 'vault', 'spark', 'verify', 'marketplace', 'blueprint', 'attestations', 'command', 'coach', 'connectors', 'omega', 'errors', 'segments', 'dashboard', 'auth', 'wizards'],
    interpolation: { escapeValue: false },
    returnNull: false,
    returnEmptyString: false,
    // Surface missing keys in dev so we notice during UI work;
    // production still falls back to EN silently.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (_lng, ns, key) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing key: ${ns}.${key}`);
      }
    },
  });

export default i18n;
